import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { RunAbout } from "../api";

/**
 * How a run tested, in one sentence: "Plays back OBA traffic recorded on a
 * vehicle, on BAWU Landscape 21." Null where the run recorded no mode - SNCF,
 * IPI-6 and io-core run one way and say nothing, and nothing is guessed.
 */
export function useHow(a: RunAbout | null | undefined): string | null {
  const { t } = useTranslation();
  if (!a || !a.mode) return null;
  if (a.fixture) return t("runAbout.howFixture");
  const layout = a.layout || t("runAbout.theLayout");
  if (a.mode === "replay") return t("runAbout.howReplay", { layout });
  if (a.mode === "simulated") return t("runAbout.howSimulated", { layout });
  return t("runAbout.howOther", { mode: a.mode, layout });
}

/**
 * The run in one sentence for its own page: how it tested where the run says,
 * else which groups of cases it covered - SNCF, IPI-6 and io-core record no
 * mode, and their runs were known only by name and reason.
 */
export function useAboutLine(a: RunAbout | null | undefined): string | null {
  const { t } = useTranslation();
  const how = useHow(a);
  if (how) return how;
  if (!a || a.groups.length === 0) return null;
  const names = a.groups.slice(0, 3).map((g) => g.name).join(", ");
  const more = a.groups.length - 3;
  return more > 0
    ? t("runAbout.lineMore", { count: a.groups.length, names, more })
    : t("runAbout.line", { count: a.groups.length, names });
}

const SHOWN = 6;

/**
 * What a run checks and how, under its name in a campaign. Campaign #4 listed
 * its four runs by profile name only, and nothing on the page said why each
 * was there. The groups are those of the cases that actually ran, those with
 * failures first; a group's own prose, where the bench sends one, opens under
 * its name.
 */
export default function RunAboutView({ about }: { about: RunAbout | null | undefined }) {
  const { t } = useTranslation();
  const how = useHow(about);
  const [all, setAll] = useState(false);
  if (!about || (!how && about.groups.length === 0)) return null;
  const groups = all ? about.groups : about.groups.slice(0, SHOWN);
  return (
    <div className="mt-1.5 max-w-3xl text-sm text-slate-700 dark:text-slate-300" data-testid="run-about">
      {how && <p>{how}</p>}
      {about.groups.length > 0 && (
        <div className="mt-1">
          <span className="text-slate-500">{t("runAbout.checks")}</span>
          <ul className="mt-0.5 space-y-0.5">
            {groups.map((g) => {
              const label = (
                <>
                  {g.name}
                  <span className="text-slate-500"> · {t("runAbout.cases", { count: g.count })}</span>
                  {g.failed > 0 && <span className="text-red-700 dark:text-red-400"> · {t("runAbout.failed", { count: g.failed })}</span>}
                </>
              );
              return (
                <li key={g.name}>
                  {g.about ? (
                    <details>
                      <summary className="cursor-pointer">{label}</summary>
                      <p className="ml-4 mt-0.5 text-slate-600 dark:text-slate-400">{g.about}</p>
                    </details>
                  ) : (
                    <span className="ml-4 block">{label}</span>
                  )}
                </li>
              );
            })}
          </ul>
          {about.groups.length > SHOWN && (
            <button type="button" className="mt-0.5 text-slate-500 underline" onClick={() => setAll(!all)}>
              {all ? t("runAbout.fewer") : t("runAbout.more", { count: about.groups.length - SHOWN })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
